/**
 * Settings, from the gear in the top bar: a window in the middle of the screen, in five tabs:
 *   Look          theme, colours, which UI (preset), the assistant's face, accessibility
 *   Conversation  how the chat looks, the debug under the replies
 *   Parts         the look of each module
 *   Screens       for each step of the task, the look of its widget (Variante 1, 2…: docs/NEW-UI/07 D-04)
 *   Screen        the margin around the screen and inside the cards, the replay frame, a few switches
 * Every choice applies at once and is saved on this device (core/appearance.ts); "back to the preset"
 * forgets them. A choice of Look changes the whole screen, so it closes Settings to show it; the other
 * tabs' details keep them open for the next one (Close, or a tap outside, closes them). The big choices are tiles with a picture (a theme shows its own colours); the others are
 * rows in grouped cards, the name on the left and a segmented control on the right.
 */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { CHAT_SCALES, CHAT_WIDTHS, DETAIL_SECONDS, FRAME_PADDINGS, LYRICS_HISTORY, PANEL_PADDINGS, ROLE_FRAMES, type Appearance } from '../../core/appearance';
import { BUILTIN_FACES, FACE_KINDS, facesHere } from '../../avatar/face';
import { BACKGROUND_AUTO, BACKGROUND_NONE, backdrop, labelOf, stageOf, type Background } from '../../stage/catalog';
import { moduleVariantsOf } from '../../modules/contract';
import { BUILTIN_THEMES, presetsFor } from '../../themes/themes';
import { accessibilityModes } from '../../core/wire';
import { MODES } from '../../modes/modes';
import { useApp } from '../../react/app';
import { useAppearance, useLanguage, useMode, useResolvedLayout, useTheme, useTranslate, useUi } from '../../react/hooks';
import { Icon } from '../../ui-kit/Icon';
import { screenChoices } from './screen-choices';
import { useUiActions } from '../useUiActions';
import css from './Settings.module.css';

const TABS = [
  { id: 'look', label: 'settings.tab.look', icon: 'palette' },
  { id: 'chat', label: 'settings.chat', icon: 'chat' },
  { id: 'parts', label: 'settings.tab.parts', icon: 'parts' },
  { id: 'screens', label: 'settings.tab.screens', icon: 'layout' },
  { id: 'screen', label: 'settings.screen', icon: 'screen' },
] as const;

type Tab = (typeof TABS)[number]['id'];

const FACE_ICONS = { musetalk: 'user', lowpoly: 'shapes', idle: 'image', particles: 'orbit' } as const;

export function SettingsSheet() {
  const open = useUi((u) => u.settingsOpen);
  const t = useTranslate();
  const actions = useUiActions();
  const [tab, setTab] = useState<Tab>('look');
  if (!open) return null;

  return (
    <div className={css.backdrop} onClick={() => actions.toggleSettings(false)}>
      <div className={css.sheet} role="dialog" aria-labelledby="settings-title" onClick={(e) => e.stopPropagation()}>
        <header className={css.header}>
          <span className={css.headerIcon}><Icon name="settings" size={30} /></span>
          <h2 id="settings-title">{t('settings.title')}</h2>
          <button className={css.close} onClick={() => actions.toggleSettings(false)} aria-label={t('overlay.close')}>
            <Icon name="close" size={30} />
          </button>
        </header>

        <nav className={css.tabs} role="tablist">
          {TABS.map((tb) => (
            <button key={tb.id} role="tab" aria-selected={tab === tb.id} className={`${css.tab} ${tab === tb.id ? css.tabOn : ''}`} onClick={() => setTab(tb.id)}>
              <Icon name={tb.icon} size={26} />
              <span>{t(tb.label)}</span>
            </button>
          ))}
        </nav>

        <div className={css.body} role="tabpanel">
          {tab === 'look' && <LookTab />}
          {tab === 'chat' && <ChatTab />}
          {tab === 'parts' && <PartsTab />}
          {tab === 'screens' && <ScreensTab />}
          {tab === 'screen' && <ScreenTab />}
        </div>

        <footer className={css.footer}>
          <button className={css.reset} onClick={() => actions.resetSettings()}>
            <Icon name="restart" size={22} />{t('settings.reset')}
          </button>
          <button className={css.done} onClick={() => actions.toggleSettings(false)}>
            <Icon name="check" size={22} />{t('overlay.close')}
          </button>
        </footer>
      </div>
    </div>
  );
}

/** The current look and the way to change it, for every tab. */
function useSettings() {
  const appearance = useAppearance();
  const actions = useUiActions();
  return {
    appearance,
    actions,
    /** `close`: the choice changes the whole screen (the Look tab's): Settings close to show it. */
    set: (patch: Partial<Appearance>, close = false) => actions.changeSettings(patch, { close }),
  };
}

function LookTab() {
  const app = useApp();
  const t = useTranslate();
  const theme = useTheme();
  const mode = useMode();
  const presetId = useUi((u) => u.presetId);
  const language = useLanguage();
  const layout = useResolvedLayout();
  const { appearance, actions, set } = useSettings();
  // the theme's layouts only; the background only where the scene shows the stage
  const layouts = presetsFor(theme, [...app.presets.keys()]).map((id) => app.presets.get(id)!);
  const showsStage = app.scenes.get(layout.preset.scene)?.stage === true;
  return (
    <>
      <Section icon="palette" title={t('settings.theme')}>
        <Tiles
          columns={2}
          options={BUILTIN_THEMES.map((th) => ({ id: th.id, label: t(th.label), picture: <ThemePicture palette={th.palettes[appearance.scheme]} /> }))}
          value={theme.id}
          onPick={(id) => actions.setTheme(id)}
        />
      </Section>
      <Section icon="contrast" title={t('settings.scheme')}>
        <Tiles
          columns={2}
          options={(['light', 'dark'] as const).map((sc) => ({
            id: sc, label: t(`settings.scheme_${sc}`), icon: sc === 'light' ? 'sun' : 'moon', picture: <ThemePicture palette={theme.palettes[sc]} />,
          }))}
          value={appearance.scheme}
          onPick={(v) => set({ scheme: v as Appearance['scheme'] }, true)}
        />
      </Section>
      {layouts.length > 1 && (
        <Section icon="layout" title={t('settings.ui')}>
          <Tiles columns={5} options={layouts.map((p) => ({ id: p.id, label: t(p.label), icon: p.icon ?? 'layout' }))} value={presetId} onPick={(id) => actions.apply(id)} />
        </Section>
      )}
      <Section icon="assistant" title={t('settings.face')}>
        <Tiles
          columns={5}
          options={[
            { id: 'auto', label: t('face.auto'), icon: 'sparkles' },
            // only the faces this screen has; the particle orb always
            ...FACE_KINDS.filter((k) => BUILTIN_FACES.includes(k) || facesHere(app.avatar, Object.keys(app.stage.avatars).length > 0)(k)).map((k) => ({ id: k, label: t(`face.${k}`), icon: FACE_ICONS[k] })),
          ]}
          value={appearance.face}
          onPick={(v) => set({ face: v as Appearance['face'] }, true)}
        />
      </Section>
      {showsStage && <Section icon="image" title={t('settings.background')}>
        <Tiles
          columns={4}
          options={[
            { id: BACKGROUND_AUTO, label: t('background.auto'), picture: <BackgroundPicture background={stageOf(app.stage, app.pack.stage, BACKGROUND_AUTO).background} /> },
            ...Object.entries(app.stage.backgrounds).map(([id, b]) => ({ id, label: labelOf(b, language, id), picture: <BackgroundPicture background={b} /> })),
            { id: BACKGROUND_NONE, label: t('background.none'), picture: <BackgroundPicture background={null} /> },
          ]}
          value={appearance.background}
          onPick={(v) => set({ background: v }, true)}
        />
      </Section>}
      <Section icon="accessibility" title={t('mode.title')}>
        <Tiles
          columns={5}
          options={accessibilityModes.map((m) => ({ id: m, label: t(MODES[m].label), icon: MODES[m].icon }))}
          value={mode.id}
          onPick={(m) => actions.setMode(m as (typeof accessibilityModes)[number])}
        />
      </Section>
    </>
  );
}

/** A stage's background in miniature (none: the theme's plain one). */
function BackgroundPicture({ background }: { background: Background | null }) {
  return <span className={css.picture} style={{ background: backdrop(background, 'var(--scene-plain, var(--color-surface-2))') }} aria-hidden />;
}

/** A tiny screen in a palette's colours: page, a card with its header, a button in the accent. */
function ThemePicture({ palette }: { palette: Record<string, string> }) {
  return (
    <span className={css.picture} style={palette as CSSProperties} aria-hidden>
      <span className={css.pictureCard}>
        <i className={css.pictureHeader} />
        <i className={css.pictureLine} />
        <i className={css.pictureLine} />
        <i className={css.pictureButton} />
      </span>
    </span>
  );
}

function ChatTab() {
  const t = useTranslate();
  const { appearance, set } = useSettings();
  const lyrics = appearance.moduleVariants.chat === 'lyrics';
  return (
    <Group>
      <Row icon="align" label={t('settings.chat_align')}>
        <Choices options={[{ id: 'side', label: t('settings.align_side') }, { id: 'center', label: t('settings.align_center') }]}
          value={appearance.chatAlign} onPick={(v) => set({ chatAlign: v as Appearance['chatAlign'] })} />
      </Row>
      <Row icon="text-size" label={t('settings.chat_size')}>
        <Choices options={CHAT_SCALES.map((v) => ({ id: String(v), label: `${Math.round(v * 100)}%` }))}
          value={String(appearance.chatScale)} onPick={(v) => set({ chatScale: Number(v) })} />
      </Row>
      <Row icon="width" label={t('settings.chat_width')}>
        <Choices options={CHAT_WIDTHS.map((v) => ({ id: String(v), label: `${v}` }))}
          value={String(appearance.chatWidth)} onPick={(v) => set({ chatWidth: Number(v) })} />
      </Row>
      {lyrics && (
        <>
          <Row icon="history" label={t('settings.lyrics_history')}>
            <Choices options={LYRICS_HISTORY.map((v) => ({ id: String(v), label: String(v) }))}
              value={String(appearance.lyricsHistory)} onPick={(v) => set({ lyricsHistory: Number(v) })} />
          </Row>
          <Toggle icon="highlight" label={t('settings.karaoke')} on={appearance.karaoke} onChange={(v) => set({ karaoke: v })} />
        </>
      )}
      <Toggle icon="speaker" label={t('settings.speak_typed')} on={appearance.speakTyped} onChange={(v) => set({ speakTyped: v })} />
      <Toggle icon="info" label={t('settings.turn_details')} on={appearance.turnDetails} onChange={(v) => set({ turnDetails: v })} />
    </Group>
  );
}

function PartsTab() {
  const app = useApp();
  const t = useTranslate();
  const { appearance, set } = useSettings();
  const modules = [...app.modules.values()].filter((m) => (m.variants?.length ?? 0) > 0);
  return (
    <>
      <Section icon="parts" title={t('settings.modules')}>
        <Group>
        {modules.map((m) => (
          <Row key={m.id} label={t(m.label)}>
            <Choices
              options={moduleVariantsOf(m).map((v) => ({ id: v.id, label: t(v.label) }))}
              value={appearance.moduleVariants[m.id] ?? 'standard'}
              onPick={(v) => set({ moduleVariants: { ...appearance.moduleVariants, [m.id]: v } })}
            />
          </Row>
        ))}
        </Group>
      </Section>
    </>
  );
}

/** Each step of the task with the looks of its widget; «Auto» is the pack's or the theme's choice. */
function ScreensTab() {
  const app = useApp();
  const t = useTranslate();
  const { appearance, set } = useSettings();
  const known = (key: string) => Object.values(app.catalogs).some((c) => c !== undefined && key in c);
  const choices = screenChoices(app.pack, app.widgets, known);
  return (
    <>
    {choices.length === 0 ? <p className={css.empty}>{t('settings.screens.none')}</p> : (
    <Section icon="layout" title={t('settings.screens.title')}>
      <Group>
        {choices.map((c) => (
          <Row key={`${c.label}/${c.widget}`} label={known(c.label) ? t(c.label) : c.label}>
            <Choices
              options={[{ id: '', label: t('settings.auto') }, ...c.variants.map((v) => ({ id: v.id, label: t(v.label) }))]}
              value={appearance.screenVariants[c.screens[0]!] ?? ''}
              onPick={(v) => set({ screenVariants: { ...appearance.screenVariants, ...Object.fromEntries(c.screens.map((s) => [s, v])) } })}
            />
          </Row>
        ))}
      </Group>
    </Section>
    )}
    {/* a card's detail a reply opened (a train's changes): how long it stays (core/detail.ts) */}
    <Section icon="clock" title={t('settings.details.title')}>
      <Group>
        <Row icon="clock" label={t('settings.details.closes')}>
          <Choices options={DETAIL_SECONDS.map((v) => ({ id: String(v), label: v === 0 ? t('settings.details.never') : t('settings.details.seconds', { n: v }) }))}
            value={String(appearance.detailSeconds)} onPick={(v) => set({ detailSeconds: Number(v) })} />
        </Row>
      </Group>
    </Section>
    </>
  );
}

function ScreenTab() {
  const app = useApp();
  const t = useTranslate();
  const { appearance, actions, set } = useSettings();
  return (
    <>
      <Group>
      <Row icon="frame" label={t('settings.frame_padding')}>
        <Choices options={FRAME_PADDINGS.map((v) => ({ id: String(v), label: v === 0 ? t('settings.none') : `${v}` }))}
          value={String(appearance.framePadding)} onPick={(v) => set({ framePadding: Number(v) })} />
      </Row>
      <Row icon="width" label={t('settings.panel_padding')}>
        <Choices options={PANEL_PADDINGS.map((v) => ({ id: String(v), label: `${v}` }))}
          value={String(appearance.panelPadding)} onPick={(v) => set({ panelPadding: Number(v) })} />
      </Row>
      {app.kiosk.role !== 'kiosk' && (
        <Row icon="play" label={t('settings.role_frame')}>
          <Choices options={ROLE_FRAMES.map((v) => ({ id: v, label: t(`settings.frame_${v}`) }))}
            value={appearance.roleFrame} onPick={(v) => set({ roleFrame: v as Appearance['roleFrame'] })} />
        </Row>
      )}
      </Group>
      {app.kiosk.role === 'kiosk' && (
        <Group>
          <Row icon="restart" label={t('session.title')}>
            <span className={css.sessionId} title={app.kiosk.sessionId}>{app.kiosk.sessionId.slice(0, 8)}</span>
            <button className={css.newSession} onClick={() => actions.newSession()}><Icon name="plus" size={20} />{t('session.new')}</button>
          </Row>
        </Group>
      )}
      <Section icon="toggles" title={t('settings.options')}>
        <div className={css.toggles}>
          <Toggle icon="hand" label={t('settings.touch_pills')} on={appearance.touchPills} onChange={(v) => set({ touchPills: v })} />
          <Toggle icon="keyboard" label={t('settings.keyboard_button')} on={appearance.keyboardButton} onChange={(v) => set({ keyboardButton: v })} />
          <Toggle icon="sparkles" label={t('settings.motion')} on={appearance.motion} onChange={(v) => set({ motion: v })} />
        </div>
        {appearance.keyboardButton && (
          <Group>
            <Row icon="keyboard" label={t('settings.keyboard_place')}>
              <Choices options={(['bottom', 'box'] as const).map((id) => ({ id, label: t(`settings.keyboard_place.${id}`) }))}
                value={appearance.keyboardPlace} onPick={(v) => set({ keyboardPlace: v as 'bottom' | 'box' })} />
            </Row>
          </Group>
        )}
      </Section>
    </>
  );
}

function Section({ icon, title, children }: { icon?: string; title: string; children: ReactNode }) {
  return (
    <section className={css.section}>
      <h3>{icon && <Icon name={icon} size={20} />}{title}</h3>
      {children}
    </section>
  );
}

/** Rows grouped in one card. */
function Group({ children }: { children: ReactNode }) {
  return <div className={css.group}>{children}</div>;
}

/** Big choices: a picture or an icon over the name, the chosen one ringed and checked. */
function Tiles({ options, value, onPick, columns }: {
  options: { id: string; label: string; icon?: string; picture?: ReactNode }[];
  value: string;
  onPick: (id: string) => void;
  columns: number;
}) {
  return (
    <div className={css.tiles} role="radiogroup" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {options.map((o) => (
        <button key={o.id} role="radio" aria-checked={o.id === value} className={`${css.tile} ${o.id === value ? css.tileOn : ''}`} onClick={() => onPick(o.id)}>
          {o.picture}
          {!o.picture && o.icon && <span className={css.tileIcon}><Icon name={o.icon} size={32} /></span>}
          <span className={css.tileLabel}>{o.picture && o.icon && <Icon name={o.icon} size={18} />}{o.label}</span>
          {o.id === value && <span className={css.tick}><Icon name="check" size={16} strokeWidth={3} /></span>}
        </button>
      ))}
    </div>
  );
}

function Row({ icon, label, children }: { icon?: string; label: string; children: ReactNode }) {
  return (
    <div className={css.row}>
      <span className={css.rowLabel}>{icon && <Icon name={icon} size={22} />}{label}</span>
      {children}
    </div>
  );
}

function Choices({ options, value, onPick }: { options: { id: string; label: string; icon?: string }[]; value: string; onPick: (id: string) => void }) {
  return (
    <div className={css.choices} role="radiogroup">
      {options.map((o) => (
        <button key={o.id} role="radio" aria-checked={o.id === value} className={`${css.choice} ${o.id === value ? css.on : ''}`} onClick={() => onPick(o.id)}>
          {o.icon && <Icon name={o.icon} size={22} />}
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Toggle({ icon, label, on, onChange }: { icon?: string; label: string; on: boolean; onChange: (on: boolean) => void }) {
  return (
    <button className={css.toggle} role="switch" aria-checked={on} onClick={() => onChange(!on)}>
      {icon && <Icon name={icon} size={22} />}
      <span className={css.toggleLabel}>{label}</span>
      <span className={`${css.switch} ${on ? css.switchOn : ''}`}><i /></span>
    </button>
  );
}
